import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { companyInfo } from '../data/mockData';
import { toast } from 'sonner';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // EmailJS configuration
      const serviceId = 'service_sndod0g';
      const templateId = 'template_64sbger';
      const publicKey = 'k-oXoPkHMWWdGCTQg';

      // Template parameters matching EmailJS template variables
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        from_phone: formData.phone,
        message: formData.message,
        to_name: 'MERX Consultoria'
      };

      // Send email using EmailJS
      const response = await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      console.log('Email sent successfully:', response);
      
      // Show success toast
      toast.success('¡Mensaje enviado exitosamente!', {
        description: 'Nos pondremos en contacto con usted muy pronto.'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending email:', error);
      
      // Show error toast
      toast.error('Error al enviar el mensaje', {
        description: 'Por favor, intente nuevamente o contáctenos directamente por email.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24" style={{ backgroundColor: 'white' }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 
            className="text-4xl font-bold mb-4"
            style={{ color: '#3B3838' }}
          >
            Contáctenos
          </h2>
          <p 
            className="text-lg"
            style={{ color: '#6B7280' }}
          >
            Estamos listos para transformar sus espacios. Solicite una cotización 
            gratuita y descubra cómo podemos ayudarle.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div>
            <h3 
              className="text-2xl font-bold mb-6"
              style={{ color: '#3B3838' }}
            >
              Información de Contacto
            </h3>
            
            <div className="space-y-6">
              {/* Email */}
              <div className="flex items-start gap-4">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#E8F2FA' }}
                >
                  <Mail size={24} style={{ color: '#2E75B6' }} />
                </div>
                <div>
                  <div 
                    className="font-semibold text-base mb-1"
                    style={{ color: '#3B3838' }}
                  >
                    Correo Electrónico
                  </div>
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="text-base hover:underline"
                    style={{ color: '#2E75B6' }}
                  >
                    {companyInfo.email}
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#F3E8FA' }}
                >
                  <Phone size={24} style={{ color: '#7030A0' }} />
                </div>
                <div>
                  <div 
                    className="font-semibold text-base mb-1"
                    style={{ color: '#3B3838' }}
                  >
                    Teléfono
                  </div>
                  <div 
                    className="text-base"
                    style={{ color: '#6B7280' }}
                  >
                    {companyInfo.phone}
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-4">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#FFF3CD' }}
                >
                  <MapPin size={24} style={{ color: '#FFC000' }} />
                </div>
                <div>
                  <div 
                    className="font-semibold text-base mb-1"
                    style={{ color: '#3B3838' }}
                  >
                    Dirección
                  </div>
                  <div 
                    className="text-base leading-relaxed"
                    style={{ color: '#6B7280' }}
                  >
                    {companyInfo.address}
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div 
              className="mt-8 p-6 rounded-xl"
              style={{ backgroundColor: '#f8f9fa' }}
            >
              <h4 
                className="font-bold text-lg mb-3"
                style={{ color: '#2E75B6' }}
              >
                Horario de Atención
              </h4>
              <div className="space-y-2 text-base" style={{ color: '#3B3838' }}>
                <div className="flex justify-between">
                  <span className="font-medium">Lunes - Viernes:</span>
                  <span>8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Sábados:</span>
                  <span>9:00 AM - 2:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Domingos:</span>
                  <span>Cerrado</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label 
                  htmlFor="name" 
                  className="block text-sm font-semibold mb-2"
                  style={{ color: '#3B3838' }}
                >
                  Nombre Completo *
                </label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Juan Pérez"
                  className="h-12 text-base"
                />
              </div>

              <div>
                <label 
                  htmlFor="email" 
                  className="block text-sm font-semibold mb-2"
                  style={{ color: '#3B3838' }}
                >
                  Correo Electrónico *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="juan@empresa.com"
                  className="h-12 text-base"
                />
              </div>

              <div>
                <label 
                  htmlFor="phone" 
                  className="block text-sm font-semibold mb-2"
                  style={{ color: '#3B3838' }}
                >
                  Teléfono *
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="33 1234 5678"
                  className="h-12 text-base"
                />
              </div>

              <div>
                <label 
                  htmlFor="message" 
                  className="block text-sm font-semibold mb-2"
                  style={{ color: '#3B3838' }}
                >
                  Mensaje *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Cuéntenos sobre sus necesidades de limpieza..."
                  rows={6}
                  className="text-base"
                />
              </div>

              <Button 
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full font-semibold text-base group"
                style={{ 
                  backgroundColor: isSubmitting ? '#94B8D6' : '#2E75B6',
                  color: 'white',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer'
                }}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                <Send 
                  size={20} 
                  className={`ml-2 ${!isSubmitting && 'group-hover:translate-x-1'} transition-transform`}
                />
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
